import * as Sentry from "@sentry/react";
import { type ComponentType, useEffect, useRef, useState } from "react";

import { reloadIfBuildReplaced } from "./staleBuild.js";

/** An overlay's component in its own chunk, which `preload` fetches once. */
export interface LazyOverlay<P> {
  /** Fetches the chunk; a failure is forgotten, so the next call tries again. */
  preload: () => Promise<ComponentType<P>>;
  /** The component, once a `preload` has resolved. */
  loaded: () => ComponentType<P> | undefined;
}

/**
 * An overlay the shell mounts closed (a dialog, a drawer, a panel), in its own chunk: none of its
 * code is in the first download, and it loads the first time it opens or, earlier, when
 * `usePreloadWhenIdle` asks. Drawn through `useLazyOverlay`.
 */
export function lazyOverlay<P extends object>(
  load: () => Promise<ComponentType<P>>,
): LazyOverlay<P> {
  let component: ComponentType<P> | undefined;
  let loading: Promise<ComponentType<P>> | undefined;
  return {
    preload: () => {
      loading ??= load().then(
        (loaded) => {
          component = loaded;
          return loaded;
        },
        (error: unknown) => {
          loading = undefined;
          throw error;
        },
      );
      return loading;
    },
    loaded: () => component,
  };
}

/**
 * Fetches an overlay's chunk once the page is idle while `wanted` (its trigger is on screen), so
 * the first open draws at once. Only the download and the module's evaluation happen early, both
 * off the page's first paint. A failure here is silent: the open tries again.
 */
export function usePreloadWhenIdle(overlay: { preload: () => Promise<unknown> }, wanted = true) {
  useEffect(() => {
    if (!wanted) return;
    const load = () => void overlay.preload().catch(() => {});
    if (typeof requestIdleCallback === "function") {
      const id = requestIdleCallback(load, { timeout: 4000 });
      return () => cancelIdleCallback(id);
    }
    const id = setTimeout(load, 2000);
    return () => clearTimeout(id);
  }, [overlay, wanted]);
}

/**
 * The overlay's component to draw, or null: null until it is first opened and its chunk is in,
 * then the component for good, so it stays mounted after it closes and its closing animation
 * plays. A chunk that fails to load while open is reported and the overlay is closed (`close`), so
 * the shopper's next open tries again; when a deploy has replaced this tab's build, the page
 * reloads onto the current one instead.
 */
export function useLazyOverlay<P>(
  overlay: LazyOverlay<P>,
  open: boolean,
  close: () => void,
): ComponentType<P> | null {
  const [fetched, setFetched] = useState<ComponentType<P>>();
  const [opened, setOpened] = useState(open);
  if (open && !opened) setOpened(true);
  const Component = fetched ?? overlay.loaded();
  const closeRef = useRef(close);
  closeRef.current = close;

  useEffect(() => {
    if (!open || Component) return;
    let live = true;
    overlay.preload().then(
      (loaded) => {
        if (live) setFetched(() => loaded);
      },
      (error: unknown) => {
        if (!live) return;
        Sentry.captureException(error);
        void reloadIfBuildReplaced(error);
        closeRef.current();
      },
    );
    return () => {
      live = false;
    };
  }, [overlay, open, Component]);

  return (opened || open) && Component ? Component : null;
}
